/**
 * The app's English is British ("finalise", "cancelled"); for businesses registered in the US
 * the English texts switch to US spelling. Derived from en.json word by word, so there is no
 * second English file to keep in sync. Switched by the company's country (components/layout/
 * app-layout.tsx), not the browser's, which says little about the business.
 */
import i18n from "i18next";
import en from "@/i18n/locales/en.json";

/** British stems and their US spelling (the endings stay). */
const STEMS: [RegExp, string][] = [
  [/\bfinalis(?=(e|ed|es|ing)\b)/gi, "finaliz"],
  [/\bcancell(?=(ed|ing)\b)/gi, "cancel"],
  [/\bcolour/gi, "color"],
  [/\bcentre/gi, "center"],
  [/\borganisation/gi, "organization"],
];

/** "Finalised" -> "Finalized", keeping the first letter's case. */
export function toUsSpelling(text: string): string {
  return STEMS.reduce(
    (result, [stem, us]) =>
      result.replace(stem, (match) =>
        match[0] === match[0]!.toUpperCase() ? us[0]!.toUpperCase() + us.slice(1) : us,
      ),
    text,
  );
}

type Tree = string | { [key: string]: Tree };
const convert = (tree: Tree): Tree =>
  typeof tree === "string"
    ? toUsSpelling(tree)
    : Object.fromEntries(Object.entries(tree).map(([key, value]) => [key, convert(value)]));

// The British texts as bundled; i18next's store holds the same objects, so never merge into it.
const british = JSON.parse(JSON.stringify(en)) as Tree;
let usEnglish: Tree | null = null;
let active = false;

/** Switches the English texts to US spelling (or back); other languages are not touched. */
export function setUsSpelling(on: boolean): void {
  if (on === active) return;
  active = on;
  usEnglish ??= convert(british);
  // Replaced section by section (not deep-merged), so the bundled objects stay untouched.
  i18n.addResourceBundle("en", "translation", on ? usEnglish : british, false, true);
  // Re-renders what is on screen when English is showing.
  if (i18n.resolvedLanguage === "en") void i18n.changeLanguage(i18n.language);
}
